import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import CommonService from '../../../services/common.service';
import { API_URLS } from '../../../constants/api-urls';
import { useFactoryDetailsMaster } from '../../../hooks/useFactoryDetailsMaster';
import { getAvailableRoles } from '../../../utils/roleSwitcher';
import { getDashboardRoute } from '../../../utils/getDashboardRoute';
import '../../../assets/styles/eway_bill_tracking.scss';
import ShippingManifestDetailsModal from '../../../components/ShippingManifestDetailsModal';
import { transformManifestData } from '../../../utils/shippingManifestView';

export default function EWayBillTracking() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('All Status');
  const [ewayBills, setEwayBills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedEWayBill, setSelectedEWayBill] = useState(null);
  const [manifestModalOpen, setManifestModalOpen] = useState(false);
  const [manifestLoading, setManifestLoading] = useState(false);
  const [selectedManifest, setSelectedManifest] = useState(null);

  const { shippingTermsOptions, modeOfTransportOptions } = useFactoryDetailsMaster();

  const cmRfqId = useMemo(() => {
    const fromState = location.state?.cmRfqId;
    if (fromState != null && fromState !== '') return Number(fromState);
    const sp = new URLSearchParams(location.search || '');
    const q = sp.get('cmRfqId');
    return q ? Number(q) : null;
  }, [location.search, location.state]);

  // This screen is tracking-only (view details + filters/search).
  const allowAcceptanceActions = false;
  const roles = getAvailableRoles();
  const isLogistics = Array.isArray(roles) && roles.some((r) => String(r?.id || '').toLowerCase() === 'logistics');

  const handleBack = () => {
    if (isLogistics) {
      navigate('/dashboard/logistics/rfqs');
      return;
    }
    navigate(getDashboardRoute());
  };

  async function syncLogisticsDeliveryStage(submissionId, quoteId, stageKey) {
    const sid = submissionId != null ? Number(submissionId) : NaN;
    const qid = quoteId != null ? Number(quoteId) : NaN;
    const key = stageKey != null ? String(stageKey).trim() : '';
    if (!Number.isFinite(sid) || sid <= 0 || !Number.isFinite(qid) || qid <= 0 || !key) return;
    try {
      await CommonService.post(API_URLS.LOGISTICS_PROVIDER.RFQ_SUBMISSION_COMPLETE_DELIVERY_STAGE(sid, qid), {
        stageKey: key,
      });
    } catch (err) {
      console.warn('Could not sync logistics delivery stage', err?.response?.data?.message || err?.message);
    }
  }

  useEffect(() => {
    fetchEWayBills();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cmRfqId]);

  useEffect(() => {
    const idRaw = location.state?.openEWayBillId;
    const id = idRaw != null ? Number(idRaw) : NaN;
    if (!Number.isFinite(id) || id <= 0) return;
    handleViewEWayBill(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state?.openEWayBillId]);

  useEffect(() => {
    if (!isLogistics) return;
    const submissionId = location.state?.logisticsSubmissionId;
    const quoteId = location.state?.logisticsQuoteId;
    const stageKey = location.state?.syncDeliveryStageKey || 'eway_track';
    syncLogisticsDeliveryStage(submissionId, quoteId, stageKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLogistics, location.state?.logisticsSubmissionId, location.state?.logisticsQuoteId]);

  const fetchEWayBills = async () => {
    try {
      setLoading(true);
      let response;
      if (cmRfqId) {
        response = await CommonService.get(`/eway-bill/cm-rfq/${cmRfqId}`);
      } else if (isLogistics) {
        try {
          response = await CommonService.get('/eway-bill/logistics/provider/my-eway-bills');
        } catch (e) {
          // Fallback to existing flow if logistics endpoint is unavailable/forbidden.
          response = await CommonService.get('/eway-bill/buyer/my-eway-bills');
        }
      } else {
        response = await CommonService.get('/eway-bill/buyer/my-eway-bills');
      }
      
      if (response && response.success && response.data) {
        // Filter out Draft status E-Way Bills
        const filteredBills = Array.isArray(response.data) 
          ? response.data.filter(ewb => {
              const status = (ewb.status || '').trim().toLowerCase();
              return status !== 'draft';
            })
          : [];
        setEwayBills(filteredBills);
      } else {
        setEwayBills([]);
      }
    } catch (error) {
      console.error('Error fetching E-Way Bills:', error);
      const msg =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        'Failed to fetch E-Way Bills';
      toast.error(msg);
      setEwayBills([]);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    return date.toISOString().split('T')[0];
  };

  const formatCurrency = (amount) => {
    if (!amount) return '₹0.00';
    return `₹${parseFloat(amount).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const getStatusClass = (status) => {
    // Tracking-only view: we present a simplified "Active" badge.
    // We still keep other states for filtering if needed, but the badge is always "Active".
    return 'ewbt-status-active';
  };

  const displayStatus = () => 'Active';

  const filteredEWayBills = ewayBills.filter(ewb => {
    const normalizedQuery = searchQuery.trim().toLowerCase();
    const searchFields = [
      ewb.ewb_number,
      ewb.document_number,
      ewb.to_name,
      ewb.vehicle_number
    ].filter(Boolean);

    const matchesSearch = normalizedQuery === '' 
      ? true 
      : searchFields.some(field => field.toLowerCase().includes(normalizedQuery));
    
    const matchesStatus = statusFilter === 'All Status' || ewb.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const statusOptions = ['All Status', 'Active', 'Accepted', 'Rejected', 'Extended', 'Completed', 'Expired', 'Cancelled'];

  // Format date for display
  const formatDateForDisplay = (dateString) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Get E-Way Bill details
  const getEWayBillDetails = async (ewayBillId) => {
    try {
      const response = await CommonService.get(`/eway-bill/${ewayBillId}`);
      if (response && response.success && response.data) {
        return response.data;
      }
      return null;
    } catch (error) {
      console.error('Error fetching E-Way Bill details:', error);
      toast.error('Failed to fetch E-Way Bill details');
      return null;
    }
  };

  // Handle View E-Way Bill
  const handleViewEWayBill = async (ewayBillId) => {
    const ewayBillDetails = await getEWayBillDetails(ewayBillId);
    if (ewayBillDetails) {
      if (isLogistics) {
        const subId =
          location.state?.logisticsSubmissionId ??
          ewayBillDetails.logistics_generic_rfq_id ??
          ewayBillDetails.logisticsGenericRfqId;
        const quoteId =
          location.state?.logisticsQuoteId ??
          ewayBillDetails.logistics_quote_id ??
          ewayBillDetails.logisticsQuoteId;
        await syncLogisticsDeliveryStage(subId, quoteId, 'eway_track');
      }
      setSelectedEWayBill(ewayBillDetails);
      setIsModalOpen(true);
    }
  };

  // Handle Close Modal
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedEWayBill(null);
  };

  const closeManifestModal = () => {
    setManifestModalOpen(false);
    setSelectedManifest(null);
  };

  const openManifestModal = async (manifestId) => {
    const id = manifestId != null ? Number(manifestId) : NaN;
    if (!Number.isFinite(id) || id <= 0) {
      toast.error('Manifest not found');
      return;
    }
    setManifestModalOpen(true);
    setManifestLoading(true);
    try {
      const res = await CommonService.get(`/shipping-manifest/${id}`);
      const raw = res?.success ? res.data : null;
      if (!raw) throw new Error(res?.message || 'Failed to fetch manifest');
      const vm = transformManifestData(raw);
      if (!vm) throw new Error('Failed to map manifest details');
      setSelectedManifest(vm);
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || 'Failed to fetch manifest';
      toast.error(msg);
      setManifestModalOpen(false);
      setSelectedManifest(null);
    } finally {
      setManifestLoading(false);
    }
  };

  return (
    <div className="eway-bill-tracking">
      <div className="ewbt-main-content">
        <div className="ewbt-header">
          <div className="ewbt-header-left">
            <button type="button" className="btn btn-link p-0 mb-2" onClick={handleBack}>
              ← Back
            </button>
            <h1 className="ewbt-main-title">E-way Bill Tracking</h1>
            <p className="ewbt-subtitle">Track and manage all E-way bills for goods transportation</p>
          </div>
        </div>

        <div className="ewbt-search-filter-section">
          <div className="ewbt-search-filter-row">
            <div className="ewbt-search-wrapper">
              <h3 className="ewbt-section-heading">Search E-way Bills</h3>
              <div className="ewbt-search-input-wrapper">
                <svg 
                  className="ewbt-search-icon" 
                  xmlns="http://www.w3.org/2000/svg" 
                  width="20" 
                  height="20" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="currentColor" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                >
                  <circle cx="11" cy="11" r="8"></circle>
                  <path d="m21 21-4.35-4.35"></path>
                </svg>
                <input
                  type="text"
                  className="ewbt-search-input"
                  placeholder="Search by EWB Number, Invoice, Consignee, or Vehicle Number..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            </div>
            <div className="ewbt-filter-wrapper">
              <h3 className="ewbt-section-heading">Filter by Status</h3>
              <select
                className="ewbt-filter-select"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                {statusOptions.map(option => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="ewbt-eway-bills-section">
          <h2 className="ewbt-eway-bills-title">E-way Bills ({filteredEWayBills.length})</h2>
          
          {loading ? (
            <div className="ewbt-empty-state">
              <p>Loading E-way Bills...</p>
            </div>
          ) : filteredEWayBills.length === 0 ? (
            <div className="ewbt-empty-state">
              <p>No E-way Bills found matching your search criteria.</p>
            </div>
          ) : (
            <div className="ewbt-eway-bill-cards">
              {filteredEWayBills.map(ewb => (
                <div key={ewb.id} className="ewbt-eway-bill-card">
                  <div className="ewbt-card-header">
                    <div className="ewbt-eway-bill-header-left">
                      <h3 className="ewbt-eway-bill-number">E-way Bill {ewb.ewb_number}</h3>
                      <span className={`ewbt-status-badge ${getStatusClass(ewb.status)}`}>
                        {displayStatus()}
                      </span>
                    </div>
                    <div className="ewbt-card-actions">
                      <button 
                        className="ewbt-action-btn ewbt-btn-view"
                        onClick={() => handleViewEWayBill(ewb.id)}
                      >
                        <svg 
                          xmlns="http://www.w3.org/2000/svg" 
                          width="16" 
                          height="16" 
                          viewBox="0 0 24 24" 
                          fill="none" 
                          stroke="currentColor" 
                          strokeWidth="2" 
                          strokeLinecap="round" 
                          strokeLinejoin="round"
                        >
                          <path d="M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0"></path>
                          <circle cx="12" cy="12" r="3"></circle>
                        </svg>
                        View
                      </button>
                      {isLogistics ? (
                        <button
                          type="button"
                          className="ewbt-action-btn ewbt-btn-view"
                          onClick={() => {
                            const manifestIdRaw = ewb.shippingManifestId ?? ewb.shipping_manifest_id ?? null;
                            const manifestId =
                              manifestIdRaw != null &&
                              String(manifestIdRaw).trim() !== '' &&
                              Number.isFinite(Number(manifestIdRaw))
                                ? Number(manifestIdRaw)
                                : null;
                            if (manifestId) {
                              openManifestModal(manifestId);
                              return;
                            }
                            navigate('/shipping-manifest-create', {
                              state: {
                                ewayBillId: ewb.id,
                                ewayNumber: ewb.ewb_number,
                                invoiceId: ewb.invoice_id ?? null,
                                invoiceNumber: ewb.document_number ?? null,
                                logisticsGenericRfqId: ewb.logistics_generic_rfq_id ?? null,
                                logisticsQuoteId: ewb.logistics_quote_id ?? null,
                              },
                            });
                          }}
                        >
                          {(ewb.shippingManifestId ?? ewb.shipping_manifest_id) ? 'View Manifest' : 'Generate Manifest'}
                        </button>
                      ) : null}
                    </div>
                  </div>

                  <div className="ewbt-card-body">
                    <div className="ewbt-details-grid">
                      <div className="ewbt-grid-item">
                        <p className="ewbt-detail-label">Invoice</p>
                        <p className="ewbt-detail-value">{ewb.document_number || '—'}</p>
                      </div>
                      <div className="ewbt-grid-item">
                        <p className="ewbt-detail-label">From</p>
                        <p className="ewbt-detail-value">{ewb.from_state || '—'}</p>
                      </div>
                      <div className="ewbt-grid-item">
                        <p className="ewbt-detail-label">To</p>
                        <p className="ewbt-detail-value">{ewb.to_state || '—'}</p>
                      </div>
                      <div className="ewbt-grid-item">
                        <p className="ewbt-detail-label">Generated</p>
                        <p className="ewbt-detail-value">{formatDate(ewb.ewb_date)}</p>
                      </div>
                      <div className="ewbt-grid-item">
                        <p className="ewbt-detail-label">Valid Upto</p>
                        <p className={`ewbt-detail-value ${ewb.status === 'Expired' ? 'ewbt-expired' : ''}`}>
                          {formatDate(ewb.valid_upto)}
                        </p>
                      </div>
                      <div className="ewbt-grid-item">
                        <p className="ewbt-detail-label">Distance</p>
                        <p className="ewbt-detail-value">{ewb.distance_km ? `${ewb.distance_km} KM` : '—'}</p>
                      </div>
                      <div className="ewbt-grid-item">
                        <p className="ewbt-detail-label">Vehicle</p>
                        <p className="ewbt-detail-value">{ewb.vehicle_number || '—'}</p>
                      </div>
                    </div>

                    {ewb.current_status && (
                      <div className="ewbt-status-info">
                        <p className="ewbt-status-text">
                          <strong>Current Status:</strong> {ewb.current_status}
                        </p>
                        {ewb.last_updated && (
                          <p className="ewbt-last-updated">
                            <strong>Last updated:</strong> {new Date(ewb.last_updated).toLocaleString()}
                          </p>
                        )}
                      </div>
                    )}

                    {ewb.status === 'Extended' && ewb.extended_count > 0 && (
                      <div className="ewbt-extended-info">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2"></path>
                        </svg>
                        <span>Extended {ewb.extended_count} time(s)</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* E-Way Bill Details View Modal */}
      {isModalOpen && selectedEWayBill && (
        <div className="ewbt-modal-overlay" onClick={handleCloseModal}>
          <div
            className="ewbt-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="ewbt-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <button 
              className="ewbt-modal-close" 
              onClick={handleCloseModal}
              aria-label="Close E-Way Bill details"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 6 6 18"></path>
                <path d="m6 6 12 12"></path>
              </svg>
            </button>

            <div className="ewbt-modal-header">
              <h2 id="ewbt-modal-title" className="ewbt-modal-title">
                E-Way Bill Details - {selectedEWayBill.ewb_number}
              </h2>
              <p className="ewbt-modal-subtitle">Complete E-Way Bill information with all transportation details</p>
            </div>

            <div className="ewbt-modal-content">
              {/* Summary Grid */}
              <div className="ewbt-summary-grid">
                <div className="ewbt-summary-item">
                  <label className="ewbt-summary-label">E-Way Bill Number</label>
                  <p className="ewbt-summary-value ewbt-value-mono">{selectedEWayBill.ewb_number || '—'}</p>
                </div>
                <div className="ewbt-summary-item">
                  <label className="ewbt-summary-label">E-Way Bill Date</label>
                  <p className="ewbt-summary-value">{formatDateForDisplay(selectedEWayBill.ewb_date)}</p>
                </div>
                <div className="ewbt-summary-item">
                  <label className="ewbt-summary-label">Valid Upto</label>
                  <p className="ewbt-summary-value">{formatDateForDisplay(selectedEWayBill.valid_upto)}</p>
                </div>
                <div className="ewbt-summary-item">
                  <label className="ewbt-summary-label">Document Number</label>
                  <p className="ewbt-summary-value ewbt-value-mono">{selectedEWayBill.document_number || '—'}</p>
                </div>
                <div className="ewbt-summary-item">
                  <label className="ewbt-summary-label">Document Date</label>
                  <p className="ewbt-summary-value">{formatDateForDisplay(selectedEWayBill.document_date)}</p>
                </div>
                <div className="ewbt-summary-item">
                  <label className="ewbt-summary-label">Status</label>
                  <div className="ewbt-summary-status">
                    <span className={`ewbt-status-badge ${getStatusClass(selectedEWayBill.status)}`}>
                      {displayStatus()}
                    </span>
                  </div>
                </div>
              </div>

              {/* Supplier/Consignor (From) Details */}
              <div className="ewbt-modal-section">
                <h3 className="ewbt-section-title">
                  <span className="ewbt-section-indicator ewbt-indicator-orange"></span>
                  Supplier / Consignor (From)
                </h3>
                <div className="ewbt-section-card ewbt-card-orange">
                  <div className="ewbt-section-grid ewbt-grid-4">
                    <div>
                      <label className="ewbt-section-label">Name</label>
                      <p className="ewbt-section-value">{selectedEWayBill.from_name || '—'}</p>
                    </div>
                    <div>
                      <label className="ewbt-section-label">GSTIN</label>
                      <p className="ewbt-section-value">{selectedEWayBill.from_gstin || '—'}</p>
                    </div>
                    <div>
                      <label className="ewbt-section-label">Country</label>
                      <p className="ewbt-section-value">{selectedEWayBill.from_country || '—'}</p>
                    </div>
                    <div>
                      <label className="ewbt-section-label">State</label>
                      <p className="ewbt-section-value">{selectedEWayBill.from_state || '—'}</p>
                    </div>
                    <div>
                      <label className="ewbt-section-label">City</label>
                      <p className="ewbt-section-value">{selectedEWayBill.from_city || '—'}</p>
                    </div>
                    <div>
                      <label className="ewbt-section-label">Pincode</label>
                      <p className="ewbt-section-value">{selectedEWayBill.from_pincode || '—'}</p>
                    </div>
                    <div style={{ gridColumn: 'span 4' }}>
                      <label className="ewbt-section-label">Address</label>
                      <p className="ewbt-section-value">{selectedEWayBill.from_address || '—'}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Buyer/Consignee (To) Details */}
              <div className="ewbt-modal-section">
                <h3 className="ewbt-section-title">
                  <span className="ewbt-section-indicator ewbt-indicator-green"></span>
                  Buyer / Consignee (To)
                </h3>
                <div className="ewbt-section-card ewbt-card-green">
                  <div className="ewbt-section-grid ewbt-grid-4">
                    <div>
                      <label className="ewbt-section-label">Name</label>
                      <p className="ewbt-section-value">{selectedEWayBill.to_name || '—'}</p>
                    </div>
                    <div>
                      <label className="ewbt-section-label">GSTIN</label>
                      <p className="ewbt-section-value">{selectedEWayBill.to_gstin || 'N/A (International)'}</p>
                    </div>
                    <div>
                      <label className="ewbt-section-label">Country</label>
                      <p className="ewbt-section-value">{selectedEWayBill.to_country || '—'}</p>
                    </div>
                    <div>
                      <label className="ewbt-section-label">State</label>
                      <p className="ewbt-section-value">{selectedEWayBill.to_state || '—'}</p>
                    </div>
                    <div>
                      <label className="ewbt-section-label">City</label>
                      <p className="ewbt-section-value">{selectedEWayBill.to_city || '—'}</p>
                    </div>
                    <div>
                      <label className="ewbt-section-label">Pincode</label>
                      <p className="ewbt-section-value">{selectedEWayBill.to_pincode || '—'}</p>
                    </div>
                    <div style={{ gridColumn: 'span 4' }}>
                      <label className="ewbt-section-label">Address</label>
                      <p className="ewbt-section-value">{selectedEWayBill.to_address || '—'}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Transportation Details */}
              <div className="ewbt-modal-section">
                <h3 className="ewbt-section-title">
                  <span className="ewbt-section-indicator ewbt-indicator-blue"></span>
                  Transportation Details
                </h3>
                <div className="ewbt-section-card ewbt-card-blue">
                  <div className="ewbt-section-grid ewbt-grid-3">
                    <div>
                      <label className="ewbt-section-label">Transport Mode</label>
                      <p className="ewbt-section-value">{selectedEWayBill.transport_mode || '—'}</p>
                    </div>
                    <div>
                      <label className="ewbt-section-label">Distance (KM)</label>
                      <p className="ewbt-section-value">{selectedEWayBill.distance_km || '—'}</p>
                    </div>
                    <div>
                      <label className="ewbt-section-label">Vehicle Type</label>
                      <p className="ewbt-section-value">{selectedEWayBill.vehicle_type || '—'}</p>
                    </div>
                    <div>
                      <label className="ewbt-section-label">Vehicle Number</label>
                      <p className="ewbt-section-value">{selectedEWayBill.vehicle_number || '—'}</p>
                    </div>
                    {selectedEWayBill.vehicle_type_description && (
                      <div>
                        <label className="ewbt-section-label">Vehicle Type Description</label>
                        <p className="ewbt-section-value">{selectedEWayBill.vehicle_type_description}</p>
                      </div>
                    )}
                    {selectedEWayBill.reason_for_transportation && (
                      <div>
                        <label className="ewbt-section-label">Reason for Transportation</label>
                        <p className="ewbt-section-value">{selectedEWayBill.reason_for_transportation}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Vehicle Update Information (Part-B) */}
              {(selectedEWayBill.previous_vehicle_number || selectedEWayBill.vehicle_update_location || selectedEWayBill.vehicle_update_reason || selectedEWayBill.vehicle_updated_at) && (
                <div className="ewbt-modal-section">
                  <h3 className="ewbt-section-title">
                    <span className="ewbt-section-indicator ewbt-indicator-indigo"></span>
                    Vehicle Update Information (Part-B)
                  </h3>
                  <div className="ewbt-section-card ewbt-card-indigo">
                    <div className="ewbt-section-grid ewbt-grid-4">
                      {selectedEWayBill.previous_vehicle_number && (
                        <div>
                          <label className="ewbt-section-label">Previous Vehicle Number</label>
                          <p className="ewbt-section-value">{selectedEWayBill.previous_vehicle_number}</p>
                        </div>
                      )}
                      <div>
                        <label className="ewbt-section-label">Current Vehicle Number</label>
                        <p className="ewbt-section-value">{selectedEWayBill.vehicle_number || '—'}</p>
                      </div>
                      {selectedEWayBill.vehicle_update_location && (
                        <div>
                          <label className="ewbt-section-label">Update Location</label>
                          <p className="ewbt-section-value">{selectedEWayBill.vehicle_update_location}</p>
                        </div>
                      )}
                      {selectedEWayBill.vehicle_updated_at && (
                        <div>
                          <label className="ewbt-section-label">Updated At</label>
                          <p className="ewbt-section-value">{new Date(selectedEWayBill.vehicle_updated_at).toLocaleString()}</p>
                        </div>
                      )}
                      {selectedEWayBill.vehicle_update_reason && (
                        <div style={{ gridColumn: 'span 4' }}>
                          <label className="ewbt-section-label">Reason for Vehicle Change</label>
                          <p className="ewbt-section-value">{selectedEWayBill.vehicle_update_reason}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Transporter Details */}
              {(selectedEWayBill.transporter_name || selectedEWayBill.transporter_gstin || selectedEWayBill.transport_document_no) && (
                <div className="ewbt-modal-section">
                  <h3 className="ewbt-section-title">
                    <span className="ewbt-section-indicator ewbt-indicator-purple"></span>
                    Transporter Details
                  </h3>
                  <div className="ewbt-section-card ewbt-card-purple">
                    <div className="ewbt-section-grid ewbt-grid-4">
                      {selectedEWayBill.transporter_name && (
                        <div>
                          <label className="ewbt-section-label">Transporter Name</label>
                          <p className="ewbt-section-value">{selectedEWayBill.transporter_name}</p>
                        </div>
                      )}
                      {selectedEWayBill.transporter_gstin && (
                        <div>
                          <label className="ewbt-section-label">Transporter GSTIN</label>
                          <p className="ewbt-section-value">{selectedEWayBill.transporter_gstin}</p>
                        </div>
                      )}
                      {selectedEWayBill.transport_document_no && (
                        <div>
                          <label className="ewbt-section-label">Transport Document No.</label>
                          <p className="ewbt-section-value">{selectedEWayBill.transport_document_no}</p>
                        </div>
                      )}
                      {selectedEWayBill.transport_document_date && (
                        <div>
                          <label className="ewbt-section-label">Transport Document Date</label>
                          <p className="ewbt-section-value">{formatDateForDisplay(selectedEWayBill.transport_document_date)}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Remarks */}
              {selectedEWayBill.remarks && (
                <div className="ewbt-modal-section">
                  <h3 className="ewbt-section-title">
                    <span className="ewbt-section-indicator ewbt-indicator-gray"></span>
                    Remarks
                  </h3>
                  <div className="ewbt-section-card ewbt-card-gray">
                    <p className="ewbt-section-value">{selectedEWayBill.remarks}</p>
                  </div>
                </div>
              )}
            </div>

            <div className="ewbt-modal-footer">
              <button 
                className="ewbt-btn ewbt-btn-secondary"
                onClick={handleCloseModal}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <ShippingManifestDetailsModal
        open={Boolean(manifestModalOpen && selectedManifest && !manifestLoading)}
        manifest={selectedManifest}
        onClose={closeManifestModal}
        shippingTermsOptions={shippingTermsOptions}
        modeOfTransportOptions={modeOfTransportOptions}
      />
    </div>
  );
}

